import React from "react";
import moment from "moment";

import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUIFileDownloadIcon from "@mui/icons-material/FileDownload";
import MUIOpenInNewIcon from '@mui/icons-material/OpenInNew';
import MUITable from "@mui/material/Table";
import MUITableBody from "@mui/material/TableBody";
import MUITableCell from "@mui/material/TableCell";
import MUITableContainer from "@mui/material/TableContainer";
import MUICollapse from '@mui/material/Collapse';
import MUITypography from "@mui/material/Typography";
import MUITableHead from "@mui/material/TableHead";
import MUITableRow from "@mui/material/TableRow";
import MUIPaper from "@mui/material/Paper";
import MUIBox from "@mui/material/Box";
import MUIButton from "@mui/material/Button";
import MUITooltip from "@mui/material/Tooltip";
import MUICheckIcon from '@mui/icons-material/Check';
import MUIGreenColor from "@mui/material/colors/green";

import Setting from "../../../Utility/Setting";
import CardClass from "../../CardClass";
import Progress from "../../Progress";
import EmptyState from "../../EmptyState";
import Menu from "../../Menu";
import Exporter from "../../../Utility/Exporter";
import User from "../../../Utility/Crud/User";
import MainContext from "../../../Context/MainContext/MainContext";

export default class CardInServiceSummaryResults extends CardClass {

    static contextType = MainContext;

    state = {
        hoveredColumn: null,
        clickedRows: {},
    };

    formatValue = (value, column) => {
        if (value === null || value === undefined) {
            return ""
        };
        if (["sum_points"].includes(column?.name)) {
            return Number.isFinite(value) ? Math.round(value) : value;
        }
        if (column?.type === "number") {
            if (value === 0) {
                return "0"
            };
            const valueAsANumber = Number(value);
            if (Number.isNaN(valueAsANumber)) {
                return value
            };
            return valueAsANumber;
        }
        return value;
    };

    getDefaultRowSx = (row) => {
        switch (row.rowType) {
            case "region-total":
            case "grand-total":
                return {
                    fontWeight: row.rowType === "region-total" ? 600 : 700,
                    bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                            ? theme.palette.primary.dark
                            : theme.palette.primary.light + "33",
                };
            default:
                return {};
        }
    };

    /**
     * Handles the click event on a row.
     * @param {*} row
     * @returns
     */
    handleRowClick = (row) => {
        if (row.rowType === "region-total" || row.rowType === "grand-total") {
            return null;
        }
        const exists = this.state.clickedRows[row.id] !== undefined;
        let newSelected;
        if (exists) {
            newSelected = {};
        } else {
            newSelected = { [row.id]: row };
        }
        this.setState({ clickedRows: newSelected });

        return;
    };


    getDefaultCellSx = (row, column) => {

        const columnWidth = this.props.defaultColumnWidths?.find(
            (defaultColumnWidth) => defaultColumnWidth.columnName === column?.name
        )?.width;

        return {
            textAlign: column.align || "left",
            whiteSpace: "nowrap",
            fontWeight:
                row.rowType && row.rowType !== "location"
                    ? 700
                    : "inherit",
            ...(columnWidth ? { width: columnWidth } : {}),
        };
    };

    /**
     *
     * @returns {JSX.Element[]} The actions for the card.
     */
    getActions() {
        return [
            <Menu
                key="export"
                menuItems={[
                    <MUIMenuItem
                        key="csv"
                        disabled={this.props.isLoading || !this.props.rows?.length}
                        onClick={() => {
                            const headers = this.props.columns.map((c) => c.title);
                            const keys = this.props.columns.map((c) => c.name);
                            const csvRows = [
                                headers,
                                ...this.props.rows.map((row) =>
                                    keys.map((key) => {
                                        if (key === "regionLocation") {
                                            return row.rowType === "region-total" ? row.region : row.location;
                                        }
                                        const value = row[key];
                                        return value == null ? "" : value;
                                    })
                                ),
                            ];
                            Exporter.exportCSV(csvRows, this.props.exportFilename);
                        }}
                    >
                        <MUIListItemIcon>
                            <MUIFileDownloadIcon fontSize="small" />
                        </MUIListItemIcon>
                        Export CSV
                    </MUIMenuItem>
                ]}
            />,
        ];
    }

    /**
     * Renders the content inside the collapsible section for a given row.
     * @param {string} header - The header title for the collapsible section.
     * @param {object} row - The data row for which the collapsible content is rendered.
     * @returns {JSX.Element|null} The rendered collapsible content or null if no row is provided.
     */
    renderCollapseContent(header, row, inOpenTerritory) {
        if (!row || !this.props.itemsGroupedByLocation || !this.props.itemsGroupedByLocation[row?.id]) {
            return null;
        }
        let items = this.props.itemsGroupedByLocation[row.id];
        if (inOpenTerritory) {
            items = items.filter(item => item.activity_AppointmentActivity_InOpenTerritory === inOpenTerritory).sort((a, b) => moment(b.activity_AppointmentActivity_ExpenseDate) - moment(a.activity_AppointmentActivity_ExpenseDate));
        } else {
            items = items.filter(item => item.activity_AppointmentActivity_SalesManagerAttended === true).sort((a, b) => moment(b.activity_AppointmentActivity_ExpenseDate) - moment(a.activity_AppointmentActivity_ExpenseDate));
        }

        return (
            <React.Fragment>
                <MUITypography sx={{ paddingTop: 1 }} variant="subtitle1" gutterBottom>
                    {header}
                </MUITypography>
                <MUITable stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
                        <MUITableHead>
                            <MUITableRow>
                                <MUITableCell colSpan={1}>Sales Rep</MUITableCell>
                                <MUITableCell colSpan={2}>Account</MUITableCell>
                                <MUITableCell colSpan={1}>Date</MUITableCell>
                                <MUITableCell colSpan={1} align="center">With Target Account</MUITableCell>
                                <MUITableCell colSpan={1} align="center">With Top Account</MUITableCell>
                            </MUITableRow>
                        </MUITableHead>
                        <MUITableBody>
                            {items.length === 0 ? (
                                <MUITableRow>
                                <MUITableCell colSpan={6}>
                                    <MUITypography variant="body2" color="textSecondary" align="center" sx={{ paddingTop: 2, paddingBottom: 2 }}>
                                        No {inOpenTerritory ? "in-services in open territories" : "in-services with reps"} for this location.
                                    </MUITypography>
                                </MUITableCell>
                            </MUITableRow>
                        ) : (
                            Object.values(items).map((row) => (
                                <MUITableRow key={row["activityId"]}>
                                    <MUITableCell colSpan={1}>{`${User.getDisplayName(this.context.usersIndexed[row["activityOwnerId"]]) || ""}`}</MUITableCell>
                                    <MUITableCell colSpan={2}>{`${row["activity_AccountName"]}`}</MUITableCell>
                                    <MUITableCell colSpan={1}>{`${moment(row["activity_AppointmentActivity_ExpenseDate"]).format("M/D/YYYY")}`}</MUITableCell>
                                    <MUITableCell colSpan={1} align="center">{row["activity_AppointmentActivity_AccountWasTarget"] ? (<MUICheckIcon fontSize="small" sx={{ color: (theme) => theme.palette.success.light }} />) : null}</MUITableCell>
                                    <MUITableCell colSpan={1} align="center">{row["activity_AppointmentActivity_WasTopAccount"] ? (<MUICheckIcon fontSize="small" sx={{ color: (theme) => theme.palette.success.light }} />) : null}</MUITableCell>
                                </MUITableRow>
                            ))
                        )}
                    </MUITableBody>
                </MUITable>
            </React.Fragment>
        );
    }

    /**
     *
     * @returns {JSX.Element} The rendered content of the card.
     */
    renderContent() {
        if (this.props.isLoading) return <Progress message="Loading Data..." />;
        const allRowsAreHidden = this.props.rows?.every(row => row.hide);
        if (!this.props.rows?.length || allRowsAreHidden) {
            return (
                <EmptyState
                    line1={
                        this.props.loadedAt ? "No results found" : "Waiting for results"
                    }
                    line2={
                        this.props.loadedAt
                            ? "Try adjusting your settings"
                            : "Please select your settings"
                    }
                />
            );
        }

        const { columns = [], rows = [] } = this.props;

        return (
            <MUIBox
                sx={{
                    paddingBottom: "none",
                    height: "calc(100vh - 240px)",
                    overflow: "none",
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
            >
                <MUITableContainer
                    component={MUIPaper}
                    elevation={0}
                    sx={{
                        height: "100%",
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <MUITable stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
                        <MUITableHead>
                            <MUITableRow>
                                {columns.map((column) => {
                                    const columnWidth = this.props.defaultColumnWidths?.find(
                                        (defaultColumnWidth) => defaultColumnWidth.columnName === column.name
                                    )?.width;

                                    return (
                                        <MUITableCell
                                            key={column.name}
                                            align={column.align || "left"}
                                            sx={{
                                                fontWeight: 700,
                                                whiteSpace: "nowrap",
                                                ...(columnWidth ? { width: columnWidth } : {}),
                                            }}
                                        >
                                            {column.title}
                                        </MUITableCell>
                                    );
                                })}
                            </MUITableRow>
                        </MUITableHead>

                        <MUITableBody>
                            {rows.map((row, rowIndex) => {
                                const baseRowSx = this.getDefaultRowSx(row);

                                if ((row.rowType === "region-total" || row.rowType === "grand-total") && row.hide) {
                                    return null;
                                }

                                if (row.rowType === "location" && row.hide) {
                                    return null;
                                }
                                return (
                                    <React.Fragment key={row.id ?? rowIndex}>
                                        <MUITableRow key={row.id ?? rowIndex}
                                            onClick={() => this.handleRowClick(row)}
                                            sx={{
                                                ...baseRowSx,
                                                "&:hover": {
                                                    backgroundColor: (theme) =>
                                                        theme.palette.action.hover,
                                                    cursor: row.rowType === "region-total" || row.rowType === "grand-total" ? "default" : "pointer",
                                                },
                                            }}>
                                            {columns.map((column) => {
                                                const raw = row[column.name];
                                                const display = column.name === "regionLocation"
                                                    ? (row.rowType === "region-total" ? row.region : row.location)
                                                    : this.formatValue(raw, column); const baseCellSx = this.getDefaultCellSx(row, column, raw);

                                                const isTierColumn = ["tier"].includes(column.name);

                                                return (
                                                    <MUITableCell key={column.name} sx={{ ...baseCellSx }}>
                                                        {isTierColumn && row.tierTooltip ? (
                                                            <MUITooltip
                                                                title={<span style={{ whiteSpace: "pre-line" }}>{row.tierTooltip}</span>}
                                                                arrow
                                                                placement="top"
                                                            >
                                                                <span>{display}</span>
                                                            </MUITooltip>
                                                        ) : (
                                                            display
                                                        )}
                                                    </MUITableCell>
                                                );
                                            })}
                                        </MUITableRow>
                                        <MUITableRow>
                                            <MUITableCell sx={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns.length}>
                                                <MUICollapse in={this.state.clickedRows[row.id] ? true : false} timeout="auto" unmountOnExit sx={{ width: "100%" }}>
                                                    {this.renderCollapseContent("In-Services With Sales Reps", row, false)}
                                                    {this.renderCollapseContent("In-Services in Open Territories", row, true)}
                                                    {row.rowType !== "grand-total" ? (
                                                        <MUIBox sx={{ display: "flex", justifyContent: "flex-end", padding: 1 }}>
                                                            <MUIButton
                                                                size="small"
                                                                variant="outlined"
                                                                endIcon={<MUIOpenInNewIcon fontSize="small" />}
                                                                onClick={() => {
                                                                    const settings = Setting.get("reports.inServiceSummary.settings") || {};
                                                                    const beginDate = moment(settings?.date.date).startOf("month") || null;
                                                                    const endDate = moment(settings?.date.date).endOf("month") || null;
                                                                    Setting.set("reports.myAccounts.autoRunReport", true);
                                                                    Setting.set("reports.myAccounts.settings", {
                                                                        dateRange: {
                                                                            beginDate: beginDate,
                                                                            endDate: endDate,
                                                                        },
                                                                        regionLocation: {
                                                                            regionIds: row.rowType === "region-total" ? [Number(row.regionId)] : null,
                                                                            locationIds: row.rowType === "location" ? (Number(row.id) === 999999 ? null : [Number(row.id)]) : null
                                                                        },
                                                                        onlyActiveAccounts: { activeAccounts: true },
                                                                        accountStatus: { status: "all" }
                                                                    });
                                                                    window.open("/sales/accounts", "_blank");
                                                                }}
                                                            >
                                                                {`View ${row?.id === 999999 ? "All" : row?.location} Accounts`}
                                                            </MUIButton>
                                                        </MUIBox>
                                                    ) : null}
                                                </MUICollapse>
                                            </MUITableCell>
                                        </MUITableRow>
                                    </React.Fragment>
                                );
                            })}
                        </MUITableBody>
                    </MUITable>
                </MUITableContainer>
            </MUIBox>
        );
    }

    getTitle() {
        return "Results";
    }
}
